import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Molecules
import { BaseCard, BaseCardHeader, PageHeader } from "@molecules/index";
// endregion Molecules
// region Atoms
import AlertDialog from "@atoms/AlertDialog";
import AlertContentByClient from "@atoms/AlertContentByClient";
import { Loading } from "@atoms/index";
// endregion Atoms
// region Store
import { api } from "@store/services/rest";
import { ModulesID } from "@store/constants";
// endregion Store
// region Graphql
import {
  ClientMutationsUpdateArgs,
  ClientType,
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";

import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
import { useAuth } from "@hooks/useAuth";
// endregion Graphql
// region Libraries
import { Grid } from "@libraries/mui";

import * as _ from "lodash";
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
import { useSelectClient } from "@atoms/SelectClient";
import { clientUpdate, TClientUpdateMutations } from "@graphql/groups/client/mutation.graphql";
import * as Styled from "./styles";
import Office from "../Office";
// endregion Libraries

const Client: React.FC = () => {

  // region States
  const [creditLimitByClient, setCreditLimitByClient] = useState<ClientType[]>([]);
  const [currentCreditLimit, setCurrentCreditLimit] = useState<ClientType[]>([]);

  const [loadingClients, setLoadingClients] = useState<boolean>(false);

  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);

  const [noPermissionInCurrentClient, setNoPermissionInCurrentClient] = useState<boolean>(false);
  // endregion States
  // region Hooks
  const { t } = useTranslation();
  const { value: selectedClient } = useSelectClient();

  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  // endregion Hooks
  // region GraphQL - Queries
  const [
    getPermissions,
    { loading, error, data: dataPermissions }
  ] = useLazyQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(
    permissionUserAllWithoutPagination([
      "construction.client.id",
      "construction.client.name",
      "construction.client.alias",
      "construction.client.document",
      "construction.client.creditLimit",
      "construction.client.creditLimitUsed",
      "construction.client.creditLimitExpiration",
      "construction.client.pendency",
      "construction.client.externalId"
    ]),
    {
      fetchPolicy: "network-only",
      pollInterval: 60000,
      variables: {
        where: [
          {
            property: "user.id",
            operator: EWhereClausesOperators.EQUALS,
            value: token.primarysid
          },
          {
            property: "module.id",
            operator: EWhereClausesOperators.EQUALS,
            value: ModulesID.CLIENT
          }
        ]
      }
    }
  );
  // endregion GraphQL - Queries
  // region GraphQL - Mutations
  const [MutationHandleCreditLimitClient] = useMutation<TClientUpdateMutations,
    ClientMutationsUpdateArgs>(clientUpdate(["id"]));
  // endregion GraphQL - Mutations
  // region Functions

  /**
   * Update all credit limit by TopconCRM endpoint
   * This is a temporary solution
   */
  const updateCreditLimit = useCallback(async (clientArray: ClientType[]) => {

    const { hostname } = window.location;
    const subDomain = hostname.split(".")[0];
    const updateCreditLimitByClient = _.cloneDeep(clientArray);

    try {

      for (const [index, client] of updateCreditLimitByClient.entries()) {

        const clientExternalId = client?.externalId;

        // eslint-disable-next-line no-await-in-loop
        const response = await api.get(
          `https://${subDomain}-remote.topconsuite.app:8090/topconweb/api/v1/contrato/limite-credito-disponivel/${clientExternalId}`,
          {
            headers: {
              "api_key_customer": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImluZnJhQHJhc3RyaW4uY29tIiwiaWQiOiJ"
                + "mM2YxNWM5ZC0xYTIwLTRmMWYtYWE5Ni0xODNhMTAxODdiNjUiLCJpYXQiOjE2MjEwMjQxMDcsImV4cCI6NDc0NTIyNjUwN30._83"
                + "mt2aSz-TKocfoB52LAGRREYEVphgMc1DVxQHQ6II"
            }
          }
        ) as any;

        if ((response?.data?.limiteCreditoTotal || response?.data?.limiteCreditoTotal === 0)
            && (response?.data?.limiteCreditoDisponivel || response?.data?.limiteCreditoDisponivel === 0)) {

          const clonedClient = { ...client };

          clonedClient.creditLimit = parseFloat(response?.data?.limiteCreditoTotal);
          clonedClient.creditLimitUsed = clonedClient.creditLimit - parseFloat(response?.data?.limiteCreditoDisponivel);

          updateCreditLimitByClient[index] = clonedClient;

          mutationHandleUpdateClient(clonedClient.id, clonedClient.creditLimit, clonedClient.creditLimitUsed);
        }
      }

    } catch (error) {
      console.log(error);
    }

    setCreditLimitByClient(updateCreditLimitByClient);
  }, [dataPermissions]);

  /**
   * Handle update client
   * @param id Id
   * @param creditLimit
   * @param creditLimitUsed
   */
  const mutationHandleUpdateClient = async (id: string, creditLimit: number, creditLimitUsed: number) => {
    await MutationHandleCreditLimitClient({
      variables: {
        client: {
          id,
          creditLimit,
          creditLimitUsed
        }
      }
    });
  };

  /**
   * Load clients
   */
  const loadClients = useCallback(async () => {
    setLoadingClients(true);

    const response = (await getPermissions()).data?.permissionUser?.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      if (response?.result?.length === 0) {
        setAlertDialogOpen(true);
      }

      // Remove duplicates
      const clientsArray = _.uniqBy(response?.result?.map((el) => el?.construction?.client), "id");

      await updateCreditLimit(clientsArray as ClientType[]);

      setLoadingClients(false);
    }
  }, [dataPermissions]);

  // endregion Functions
  // region UseEffects

  // Load clients on initial render
  useEffect(() => {

    if (!dataPermissions) loadClients().then();
    if (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    }
  }, [loadClients, dataPermissions, error, addToast]);

  // Filter data by selected client and verify if user has permission
  useEffect(() => {

    if (loading) return;

    const filteredData = creditLimitByClient.filter((el) => el.id === selectedClient?.id);

    setCurrentCreditLimit(filteredData);

    if (filteredData.length <= 0) setNoPermissionInCurrentClient(true);
    else setNoPermissionInCurrentClient(false);

  }, [dataPermissions, selectedClient, setNoPermissionInCurrentClient, loading, creditLimitByClient]);

  // endregion UseEffects

  return (
    <Office>
      <PageHeader title={t(CustomerMessages.applicationClient)} url="/" />
      <Styled.Container>
        <AlertDialog
          open={alertDialogOpen}
          title={t(AlertMessages.noPermissionCreditLimit)}
          onClose={() => setAlertDialogOpen(false)}
          iconColor="red"
        />
        <Grid container spacing={2}>
          {loadingClients && (
            <Grid item xs={12}>
              <Loading />
            </Grid>
          )}
          {!loadingClients
            && currentCreditLimit.map((client) => (
              <Grid key={`grid-${client.id}`} item xs={12} sm={6} md={4}>
                <BaseCard
                  key={client.id}
                  id={client.id}
                  header={<BaseCardHeader type="client" data={client} />}
                />
              </Grid>
            ))}
          {!loadingClients && noPermissionInCurrentClient && (
            <AlertContentByClient
              alertMessage={t(AlertMessages.noPermissionCreditLimitSpecificClient)}
              iconColor="red"
            />
          )}
        </Grid>
      </Styled.Container>
    </Office>
  );
};

export default Client;
